import React, { useState, useRef, useEffect } from "react";
import { FaFigma, FaArrowLeft, FaSignOutAlt, FaUser, FaChevronDown } from "react-icons/fa";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PATH_LOGIN } from "../constants";
import { useAuth } from "../hooks/useAuth";
import { PathParameters } from "../types/path-parameters";
import { getRoleName, removeToken } from "../utils/auth-utils";

const Navbar: React.FC = () => {
  const { userData, setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<PathParameters>();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    removeToken();
    setAuth(null);
    navigate(PATH_LOGIN);
    toast.info("Successfully logged out!");
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if the current path is a dynamic segment for SingleDesignView or SingleFrameView
  // Determine the target route for the back button
  const getBackNavigationPath = () => {
    if (
      location.pathname.startsWith(`/${params.design_id}/${params.frame_id}`)
    ) {
      // User is on SingleFrameView
      return { path: `/${params.design_id}`, name: "Page list" }; // Navigate to SingleDesignView
    } else if (location.pathname.startsWith(`/${params.design_id}`)) {
      // User is on SingleDesignView
      return { path: "/", name: "Dashboard" }; // Navigate to Dashboard
    }
    return null; // No back navigation needed
  };

  const backNavigationPath = getBackNavigationPath();
  const userInitials = userData?.email ? userData.email.charAt(0).toUpperCase() : 'U';

  return (
    <nav className="navbar">
      <div className="container">
        <div className="navbar-container">
          {/* Left Side - Logo and Navigation */}
          <div className="navbar-left">
            <Link className="navbar-brand" to="/">
              <div className="brand-icon">
                <FaFigma />
              </div>
              <span className="brand-text">Figma Press</span>
            </Link>
            
            {backNavigationPath && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => navigate(backNavigationPath.path)}
                title={`Go back to ${backNavigationPath.name}`}
              >
                <FaArrowLeft />
                <span className="back-text">Back to {backNavigationPath.name}</span>
              </button>
            )}
          </div>

          {/* Right Side - User Profile */}
          <div className="navbar-right">
            {userData && (
              <div className="user-profile" ref={dropdownRef}>
                <button 
                  className="profile-button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                >
                  <div className="user-avatar">
                    {userInitials}
                  </div>
                  <div className="user-info-compact">
                    <span className="user-name">{userData.email}</span>
                    <span className="user-role">{getRoleName(userData.role)}</span>
                  </div>
                  <FaChevronDown className={`dropdown-icon ${isDropdownOpen ? 'open' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="profile-dropdown">
                    <div className="dropdown-header">
                      <div className="user-avatar large">
                        {userInitials}
                      </div>
                      <div className="user-details-dropdown">
                        <span className="user-email-dropdown">{userData.email}</span>
                        <span className="user-role-dropdown">{getRoleName(userData.role)}</span>
                      </div>
                    </div>
                    <div className="dropdown-divider"></div>
                    <button 
                      className="dropdown-item logout-item"
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
